import {
    Box,
    Button,
    Checkbox,
    Divider,
    Flex,
    Icon,
    Modal,
    ModalBody,
    ModalContent,
    ModalFooter,
    ModalOverlay,
    Text,
    useDisclosure,
    VStack,
} from "@chakra-ui/react";
import { WarningTwoIcon } from "@chakra-ui/icons";
import Cookies from "js-cookie";
import React from "react";
import { Link } from "../Link";
import mixpanel from "mixpanel-browser";
import { ThemeColor } from "../../theme/palette";
import { getAlphaColor } from "../../theme/utils";

const TERMS_COOKIE_NAME = "terms-accepted-date";

const trackTermsOpen = () => {
    mixpanel.track("Open Terms", {
        source: "terms_modal",
        type: "protocol",
    });
};

export const TermsModal = () => {
    const { isOpen, onOpen, onClose } = useDisclosure();
    const [accepted, setAccepted] = React.useState(!!Cookies.get(TERMS_COOKIE_NAME));

    const onEnter = () => {
        Cookies.set(TERMS_COOKIE_NAME, new Date().toUTCString(), { expires: 3650 });
        setAccepted(true);
        onClose();
    };

    React.useEffect(() => {
        !accepted && onOpen();
    }, [accepted, onOpen]);

    return (
        <Modal
            closeOnOverlayClick={false}
            isOpen={isOpen}
            onClose={onClose}
            autoFocus={false}
            scrollBehavior="inside"
            isCentered
        >
            <ModalOverlay bg="blackAlpha.700" backdropFilter="blur(4px)" />
            <ModalContent
                bg="#131B26"
                border="1px solid"
                borderColor="whiteAlpha.200"
                borderRadius="2xl"
                color="white"
                w="100%"
                maxW={{ base: "calc(100% - 2rem)", md: "560px" }}
                mx={4}
                maxH="90vh"
                overflow="hidden"
            >
                <ModalBody px={{ base: 5, md: 6 }} pt={6} pb={4}>
                    <Flex align="center" gap={4} mb={5}>
                        <Flex
                            align="center"
                            justify="center"
                            flexShrink={0}
                            w={11}
                            h={11}
                            borderRadius="xl"
                            bg={getAlphaColor(ThemeColor.KAIROS, 0.12)}
                            border="1px solid"
                            borderColor={getAlphaColor(ThemeColor.KAIROS, 0.35)}
                        >
                            <Icon as={WarningTwoIcon} boxSize={5} color={ThemeColor.KAIROS} />
                        </Flex>
                        <Box>
                            <Text fontSize="lg" fontWeight="semibold" lineHeight="short" mb={1}>
                                Before you continue
                            </Text>
                            <Text fontSize="sm" color="whiteAlpha.600" lineHeight="tall">
                                Please read and accept the terms to use this interface.
                            </Text>
                        </Box>
                    </Flex>

                    <VStack align="stretch" spacing={4}>
                        <Text fontSize="sm" color="whiteAlpha.800" lineHeight="tall">
                            No representation or warranty is made concerning any aspect of the Kyros
                            Protocol, including its suitability, quality, availability, accessibility,
                            accuracy or safety. As more fully explained in the Terms of Use (available{" "}
                            <Link
                                to="/protocol-terms.pdf"
                                color={ThemeColor.KAIROS}
                                target="_blank"
                                onClick={trackTermsOpen}
                            >
                                here
                            </Link>
                            ), your access to and use of the Kyros Protocol through this Interface is
                            entirely at your own risk and could lead to substantial losses, for which you
                            take full responsibility.
                        </Text>

                        <Box
                            px={4}
                            py={3}
                            borderRadius="xl"
                            bg="#0c1117"
                            border="1px solid"
                            borderColor="whiteAlpha.100"
                            maxH="140px"
                            overflowY="auto"
                        >
                            <Text fontSize="xs" fontWeight="semibold" color="whiteAlpha.700" mb={2} textTransform="uppercase" letterSpacing="wider">
                                Restricted jurisdictions
                            </Text>
                            <Text fontSize="sm" color="whiteAlpha.700" lineHeight="tall">
                                This Interface is not available to residents of Belarus, Burundi, the
                                Central African Republic, the Democratic Republic of Congo, the Democratic
                                People&apos;s Republic of Korea, the temporarily occupied regions of Ukraine,
                                Cuba, Iran, Libya, the People&apos;s Republic of China, the Russian Federation,
                                Somalia, Sudan, South Sudan, Syria, the United States of America (including
                                its overseas territories), Venezuela, Yemen, and Zimbabwe or any other
                                jurisdiction in which accessing or using the Kyros Protocol is prohibited
                                (&quot;Prohibited Jurisdictions&quot;). In using this Interface, you confirm that
                                you are not located in, incorporated or otherwise established in, resident, or
                                a citizen of a Prohibited Jurisdiction.
                            </Text>
                        </Box>
                    </VStack>
                </ModalBody>

                <Divider borderColor="whiteAlpha.100" />

                <ModalFooter flexDirection="column" gap={4} px={{ base: 5, md: 6 }} py={5}>
                    <Checkbox
                        isChecked={accepted}
                        onChange={({ target }) => setAccepted(target.checked)}
                        colorScheme="cyan"
                        size="lg"
                        alignItems="flex-start"
                        sx={{
                            ".chakra-checkbox__control": {
                                borderColor: "whiteAlpha.400",
                                _checked: {
                                    bg: ThemeColor.KAIROS,
                                    borderColor: ThemeColor.KAIROS,
                                    color: "black",
                                },
                            },
                        }}
                    >
                        <Text fontSize="sm" color="whiteAlpha.800" lineHeight="tall" ml={1}>
                            I confirm that I have read, understand and accept the{" "}
                            <Link
                                to="/protocol-terms.pdf"
                                color={ThemeColor.KAIROS}
                                target="_blank"
                                onClick={trackTermsOpen}
                            >
                                Terms of Use
                            </Link>
                        </Text>
                    </Checkbox>

                    <Button
                        w="100%"
                        size="md"
                        h={12}
                        borderRadius="xl"
                        fontWeight="semibold"
                        onClick={onEnter}
                        isDisabled={!accepted}
                        bg={ThemeColor.KAIROS}
                        color="black"
                        _hover={{
                            bg: ThemeColor.KAIROS,
                            filter: "brightness(1.08)",
                        }}
                        _disabled={{
                            bg: "whiteAlpha.200",
                            color: "whiteAlpha.500",
                            cursor: "not-allowed",
                        }}
                    >
                        Enter Kyros
                    </Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
};
